import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Home,
  Briefcase,
  MapPin,
  Star,
  ArrowRight,
  Users
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const IndialanticPage = () => {
  const services = [
    {
      title: "Recurring House Cleaning",
      description: "Professional cleaning for busy Indialantic households. Weekly or bi-weekly service with the same trusted team."
    },
    {
      title: "Deep Cleaning",
      description: "Comprehensive cleaning for beachside homes dealing with salt air and sand. Perfect for seasonal refreshes."
    },
    {
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for Indialantic home transitions, helping both renters and homeowners."
    },
    {
      title: "Vacation Rental Cleaning",
      description: "Reliable turnover cleaning for beachside vacation rentals with professional standards."
    }
  ];

  const faqs = [
    {
      question: "Do you serve all of Indialantic?",
      answer: "Yes. We serve all Indialantic neighborhoods—from the oceanfront homes along A1A to the residential areas west of US-1, including neighborhoods near Gleason Park and the Indialantic By The Sea community. We know the area well."
    },
    {
      question: "How do you handle beachside cleaning challenges?",
      answer: "Indialantic homes face salt air, sand, and humidity year-round. We pay extra attention to window tracks, screens, and outdoor-facing surfaces. Regular maintenance prevents buildup that can damage finishes over time."
    },
    {
      question: "What makes Pierce Premier different from other cleaning companies in Indialantic?",
      answer: "We're locally owned by Bob and Heather, not a franchise. You'll work directly with us and see the same cleaners every visit. Heather's 15+ years of experience means we deliver professional results consistently."
    },
    {
      question: "Can you work around our schedule?",
      answer: "Absolutely. Many of our Indialantic clients are professionals with demanding schedules. We're flexible and work around your availability."
    },
    {
      question: "Do you clean vacation rental properties in Indialantic?",
      answer: "Yes. We handle turnovers for beachside vacation rentals throughout Indialantic. Fast, reliable, and guest-ready."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Indialantic, FL | Pierce Premier",
      description: "Professional house cleaning in Indialantic, FL. Beachside specialists serving professional families. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/indialantic"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Indialantic" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Indialantic",
      description: "Professional house cleaning services in Indialantic, FL including recurring cleaning, deep cleaning, move-in/move-out, and vacation rental turnover."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Indialantic, FL | Pierce Premier"
        description="Professional house cleaning in Indialantic, FL. Beachside specialists for busy families. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/indialantic"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        <section className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <Badge className="bg-indigo-600 text-white">Beachside Professional</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Indialantic, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Indialantic blends beachside living with professional family life. It's close to Melbourne's job centers, has great schools, and offers that coastal lifestyle without the tourist crowds. Pierce Premier serves this community with cleaning services designed for busy households who value quality and consistency.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you're managing a beachfront property, raising a family near Gleason Park, or working from home while juggling everything else, we're here to take cleaning off your plate.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-indigo-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-indigo-600 text-indigo-600 px-8 py-4 rounded-lg font-semibold hover:bg-indigo-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional house cleaning in Indialantic, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Indialantic Families Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Professional cleaning for professional families who value their time and their homes.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Built for Busy Schedules</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Many Indialantic residents are professionals balancing demanding careers with family life. We're flexible, reliable, and easy to work with—cleaning fits into your schedule, not the other way around.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. You'll have the same cleaners every visit, so they learn your home and your preferences. That consistency matters when you're trusting someone in your space.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Beachside Expertise</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Living near the ocean means dealing with sand, salt air, and humidity. Heather's 15+ years of experience includes plenty of beachside homes, so we know how to protect your investment.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Bob and Heather personally oversee every job. We're not a franchise—we're a local business that stands behind our work.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-2">
              <CardContent className="p-8 md:p-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Why We Love Serving Indialantic
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Indialantic feels like a place where people have found that balance—close to work in Melbourne, close to the beach, and close to good schools. It's the kind of community where families put down roots.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We appreciate the opportunity to serve Indialantic residents who value professionalism and consistency. When you're busy managing careers, kids, and everything in between, having one less thing to worry about makes a difference.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Cleaning Services for Indialantic Homes
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => (
                <Card key={idx} className="border-2 hover:border-indigo-200 transition-colors">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Indialantic Areas We Serve
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Oceanfront A1A",
                "Gleason Park Area",
                "Indialantic By The Sea",
                "Fifth Avenue",
                "US-1 Corridor",
                "Wavecrest"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Code:</span> 32903
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                We Also Serve Nearby Communities
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { name: "Melbourne Beach", link: "#" },
                { name: "Satellite Beach", link: "/areas/satellite-beach" },
                { name: "Indian Harbour Beach", link: "/areas/indian-harbour-beach" },
                { name: "Melbourne", link: "/areas/melbourne" },
                { name: "West Melbourne", link: "#" },
                { name: "Palm Bay", link: "/areas/palm-bay" }
              ].map((city, idx) => (
                city.link === "#" ? (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 text-center">
                    <span className="text-gray-600">{city.name}</span>
                  </div>
                ) : (
                  <Link key={idx} to={city.link} className="p-4 bg-indigo-50 rounded-lg border-2 border-indigo-200 hover:border-indigo-400 transition-colors text-center group">
                    <span className="text-indigo-700 font-semibold group-hover:text-indigo-900">{city.name}</span>
                    <ArrowRight className="w-4 h-4 inline-block ml-1 text-indigo-600" />
                  </Link>
                )
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Indialantic Residents
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-indigo-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;Our vacation rental always looks perfect thanks to Pierce Premier. Guests constantly compliment the cleanliness!&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">Jennifer Martinez</p>
                <p className="text-gray-500 text-sm">Indialantic Area</p>
              </CardContent>
            </Card>
          </div>
        </section>

        <CTASection 
          title="Ready to Simplify Your Life?"
          description="Let Pierce Premier handle the cleaning while you focus on what matters. Professional service, consistent team, and quality you can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default IndialanticPage;